import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { getCollectionStatsApi, getCollectionStatsHistoryApi } from "../../redux/api/clicksApi";
import { message } from "antd";
import moment from "moment";

const CollectionStats = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Filters and Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [activeFilter, setActiveFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_removal_date');
  const [order, setOrder] = useState('DESC');

  // Modal State
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCollectionStatsApi({
        page: page + 1,
        limit: rowsPerPage,
        active: activeFilter,
        search,
        sortby: sortBy,
        order
      });
      if (res.data?.success) {
        setData(res.data.data);
        setTotal(res.data.total);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load collection stats");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, activeFilter, sortBy, order]);

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (page !== 0) setPage(0);
      else fetchData();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const fetchHistory = async (campId, collectionName) => {
    setSelectedCamp({ id: campId, name: collectionName });
    setOpenHistory(true);
    setHistoryLoading(true);
    try {
      const res = await getCollectionStatsHistoryApi(campId);
      if (res.data?.success) {
        setHistoryData(res.data.data);
      }
    } catch (err) {
      message.error("Failed to load history");
    }
    setHistoryLoading(false);
  };

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Collection Stats Reference
      </Typography>

      <Paper sx={{ p: 2, mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search by Name or Camp ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={activeFilter}
            label="Status"
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Removed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="last_removal_date">Last Removal Date</MenuItem>
            <MenuItem value="avg_user_count">Avg User Count</MenuItem>
            <MenuItem value="collection_name">Collection Name</MenuItem>
            <MenuItem value="camp_id">Campaign ID</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Order</InputLabel>
          <Select
            value={order}
            label="Order"
            onChange={(e) => setOrder(e.target.value)}
          >
            <MenuItem value="DESC">Descending</MenuItem>
            <MenuItem value="ASC">Ascending</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Camp ID</strong></TableCell>
              <TableCell><strong>Collection Name</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Avg Users</strong></TableCell>
              <TableCell><strong>Last Start</strong></TableCell>
              <TableCell><strong>Last Removal</strong></TableCell>
              <TableCell><strong>Warning</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Loading...</TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((row) => (
                <TableRow 
                  key={row.camp_id} 
                  hover 
                  style={{ cursor: 'pointer', backgroundColor: row.tag_active === 0 ? '#fff0f0' : 'inherit' }}
                  onClick={() => fetchHistory(row.camp_id, row.collection_name)}
                >
                  <TableCell>{row.camp_id}</TableCell>
                  <TableCell>{row.collection_name}</TableCell>
                  <TableCell>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      backgroundColor: row.tag_active === 1 ? '#e6f4ea' : '#fce8e6',
                      color: row.tag_active === 1 ? '#137333' : '#c5221f',
                      fontWeight: 'bold',
                      fontSize: '0.85em'
                    }}>
                      {row.tag_active === 1 ? 'Active' : 'Removed'}
                    </span>
                  </TableCell>
                  <TableCell>{row.avg_user_count}</TableCell>
                  <TableCell>{row.last_start_date ? moment(row.last_start_date).format('YYYY-MM-DD') : '-'}</TableCell>
                  <TableCell>{row.last_removal_date ? moment(row.last_removal_date).format('YYYY-MM-DD') : '-'}</TableCell>
                  <TableCell style={{ color: '#d93025' }}>{row.warning_message || '-'}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">No stats found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Dialog 
        open={openHistory} 
        onClose={() => setOpenHistory(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedCamp ? `Last 30 Days History: ${selectedCamp.name} (ID: ${selectedCamp.id})` : ''}
          <IconButton onClick={() => setOpenHistory(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Server</strong></TableCell>
                  <TableCell><strong>Count</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow><TableCell colSpan={3} align="center">Loading...</TableCell></TableRow>
                ) : historyData.length > 0 ? (
                  historyData.map((h, i) => (
                    <TableRow key={i}>
                      <TableCell>{moment(h.date).format('YYYY-MM-DD')}</TableCell>
                      <TableCell>{h.server_id}</TableCell>
                      <TableCell>{h.count}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow><TableCell colSpan={3} align="center">No recent history</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CollectionStats;
